import { isEqual } from "lodash";

/**
 * チェック対象に値が存在するか判定する。
 * @param v チェック対象
 * @return {boolean} nullまたはundefinedの場合:true
 * 値が存在する場合:false
 */
export function isNullish<T>(v: T | null | undefined): v is null | undefined {
  return v === null || v === undefined;
}

/**
 * チェック対象に値が存在するか判定する。
 * @param v チェック対象
 * @return {boolean} 値が存在する場合:true
 * nullまたはundefinedの場合:false
 */
export function hasValue<T>(v: T | null | undefined): v is T {
  return !isNullish(v);
}

/**
 * 配列の中に値が存在するか判定する。
 *
 * @param array チェック対象の配列
 * @return {boolean}
 * 対象の配列に値が存在する場合:true
 * 配列が空の場合:false
 */
export function isNotEmpty<T>(array: T[] | null | undefined): array is T[] {
  return hasValue(array) ? array.length > 0 : false;
}

export const isAnyValueOf =
  <T extends readonly (string | number)[]>(values: T) =>
  (value: unknown): value is T[number] =>
    values.some((v) => value === v);

/**
 * 2つの配列を並び順を無視して比較し、配列の中身が同一か判定する。
 * @param arr0 チェック対象の配列
 * @param arr1 チェック対象の配列
 * @param useDeepEqual 比較にdeep equalityを用いるならtrue。
 * @return {boolean} 2つの配列を比較して中身が全て同一の場合:true
 */
export function isEqualArrayIgnoringOrder<T>(
  arr0: T[],
  arr1: T[],
  useDeepEqual: boolean = false,
) {
  // 配列の要素数が異なる場合は異なる配列と判定
  if (arr0.length !== arr1.length) {
    return false;
  }

  // 要素の並び順を揃える
  const sortedBase = arr0.concat().sort();
  const sortedCompare = arr1.concat().sort();

  const _isEqual = useDeepEqual ? isEqual : (v0: T, v1: T) => v0 === v1;

  let index = sortedBase.length;
  while (index--) {
    // 要素が異なる場合、その時点で結果を返却
    if (!_isEqual(sortedBase[index], sortedCompare[index])) {
      return false;
    }
  }
  return true;
}
